import {Screens} from '../../../../../shared/routing';
import View from './view';
import {change, getFormValues, initialize, reduxForm, setSubmitFailed, submit} from 'redux-form';
import {Forms} from '../../../../../shared/models/forms';
import React, {createElement, useEffect} from 'react';
import {updateProfileSettings} from '../../../../../redux/actions/profile';
import {useSelector} from 'react-redux';
import {useAppDispatch} from '../../../../../redux';
import {IStateProps} from './model';
import {profileSelector, profileSettingsSelector} from '../../../../../redux/selectors/profile';
import {validationPipe} from '../../../../../shared/pipes';
import {RegExps} from '../../../../../shared/constants/regexps';
import {UpdateProfileSettingsRequestPayload} from '../../../../../shared/models/api';
import {useNavigation, useRoute} from '@react-navigation/native';
import {Modal} from '../../../../../shared/components/modal/modal';
import {ProfileChangePassword} from '../../../common/changePassword';
import {IChangePasswordForm} from '../../../common/changePassword/model';
import {LoadableContent} from '../../../../../shared/components/loadableContent';
import {useConfirmEmail} from '../../../../../shared/hooks/useConfirmEmail';
import {isChangeEmailConfirmation, isChangeEmailOp} from '../../../../../shared/hooks/useConfirmationDeepLink';
import {Forms as LoginForms} from '../../../../../shared/models/forms';
import {PasswordCreationForm} from '../passwordCreationForm/PasswordCreationForm';

interface DesktopProfileSettingsRoute {
  params?: {
    op?: string;
    code?: string;
  };
}

const getFormErrors = (formData: any) =>
  validationPipe(formData, [
    {
      names: 'PhoneNumber',
      error: 'Не корректный формат номера',
      type: 'pattern',
      pattern: RegExps.phoneNumber,
    },
    {
      names: 'Email',
      error: 'Не корректный формат почты',
      type: 'pattern',
      pattern: RegExps.email,
    },
  ]);

const Form = reduxForm<UpdateProfileSettingsRequestPayload, IStateProps>({
  form: Forms.desktopSettings,
})(View);

export const DesktopProfileSettings: React.FC = () => {
  const route = useRoute<any>();
  const params = (route.params as DesktopProfileSettingsRoute['params']) || {};
  const {op = '', code = ''} = params;
  const dispatch = useAppDispatch();
  const isEmailConfirmationNeeded = React.useMemo(() => isChangeEmailConfirmation(op, code), [op, code]);
  const isPasswordCreationNeeded = React.useMemo(() => op.toLowerCase() === 'createpassemail' && code, [op, code]);
  const [isLoading, setIsLoading] = React.useState(isEmailConfirmationNeeded);
  const [activeModal, setActiveModal] = React.useState<string>('');
  const navigation = useNavigation();

  useEffect(() => {
    const _opName = op.toLowerCase();
    if (_opName === 'restorepassemail' || isChangeEmailOp(op)) {
      navigation.reset({
        index: 2,
        routes: [{name: Screens.Main}, {name: Screens.DesktopProfileMe}, {name: Screens.DesktopProfileSettings}],
      });
    }
  }, [op]);

  useEffect(() => {
    if (isPasswordCreationNeeded) {
      setActiveModal('create-password');
    }
  }, [isPasswordCreationNeeded]);

  useConfirmEmail(isEmailConfirmationNeeded, code, setIsLoading);

  const profileSettings = useSelector(profileSettingsSelector());
  const profile = useSelector(profileSelector());
  const formValues = useSelector(getFormValues(Forms.desktopSettings));

  const hasPassword = React.useMemo(() => {
    return profile?.holder?.hasPassword ?? true;
  }, [profile.holder?.hasPassword]);

  useEffect(() => {
    dispatch(initialize(Forms.desktopSettings, profileSettings));
  }, [profileSettings]);

  const hideModal = () => {
    if (isPasswordCreationNeeded) {
      navigation.reset({
        index: 2,
        routes: [{name: Screens.Main}, {name: Screens.DesktopProfileMe}, {name: Screens.DesktopProfileSettings}],
      });
    }
    setActiveModal('');
  };

  const handleChangePassword = () => {
    const initialFormData: IChangePasswordForm = {
      confirmPassword: '',
      newPassword: '',
      oldPassword: '',
    };

    dispatch(initialize(Forms.changePassword, initialFormData));
    setActiveModal('change-password');
  };

  const handleCreatePassword = () => {
    const initialFormData = {
      password: '',
      confirm: '',
    };

    dispatch(initialize(LoginForms.registrationOrRecoveryForm, initialFormData));
    setActiveModal('create-password');
  };

  if (!profileSettings) {
    return null;
  }

  return (
    <>
      <LoadableContent loading={isLoading}>
        {createElement(Form, {
          profileSettings,
          hasPassword,
          onPressChangePassword: handleChangePassword,
          onPressCreatePassword: handleCreatePassword,
          onSwitchContactsPhone(event) {
            if (event.name === 'revert') {
              dispatch(change(Forms.desktopSettings, 'PhoneNumber', profileSettings.phoneNumber));
            }

            if (event.name === 'swap' && event.nextView === 'initial') {
              const errors = getFormErrors(formValues);
              if (errors) {
                dispatch(setSubmitFailed(Forms.desktopSettings, errors));
                if (errors?.hasOwnProperty('PhoneNumber')) {
                  return false;
                }
              }

              dispatch(submit(Forms.desktopSettings));
            }
          },
          onSwitchContactsEmail(event) {
            if (event.name === 'revert') {
              dispatch(change(Forms.desktopSettings, 'Email', profileSettings.email));
            }

            if (event.name === 'swap' && event.nextView === 'initial') {
              const errors = getFormErrors(formValues);
              if (errors) {
                dispatch(setSubmitFailed(Forms.desktopSettings, errors));
                if (errors?.hasOwnProperty('Email')) {
                  return false;
                }
              }

              dispatch(submit(Forms.desktopSettings));
            }
          },
          onSubmit(formData: UpdateProfileSettingsRequestPayload) {
            dispatch(updateProfileSettings(formData));
          },
        })}
      </LoadableContent>

      {activeModal === 'change-password' && (
        <Modal title='Изменение пароля' onClose={hideModal}>
          <ProfileChangePassword onFormSubmitted={hideModal} />
        </Modal>
      )}

      {activeModal === 'create-password' && (
        <Modal title='Создание пароля' onClose={hideModal}>
          <PasswordCreationForm onSubmit={hideModal} initialCodeProps={isPasswordCreationNeeded ? code : undefined} />
        </Modal>
      )}
    </>
  );
};
